import { useState, type FormEvent } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { entorno, esProductivo, etiquetaAmbiente } from '@/lib/entorno'
import { LogoMega } from '@/shared/marca/LogoMega'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { Campo } from '@/shared/ui/Formulario/Campo'

import { useSesion } from './useSesion'
import estilos from './PaginaIngreso.module.css'

export function PaginaIngreso() {
  const { perfil, cargando, ingresar } = useSesion()
  const ubicacion = useLocation()

  const [usuario, setUsuario] = useState('')
  const [contrasena, setContrasena] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [entrando, setEntrando] = useState(false)

  if (cargando) {
    return <Cargando pantallaCompleta mensaje="AUTENTICANDO ACCESO SEGURO..." />
  }

  // Quien ya entró vuelve a donde iba antes de que lo mandaran a ingresar.
  if (perfil) {
    const destino = (ubicacion.state as { desde?: string } | null)?.desde ?? '/'
    return <Navigate to={destino} replace />
  }

  async function manejarEnvio(evento: FormEvent) {
    evento.preventDefault()
    setError(null)
    setEntrando(true)
    try {
      await ingresar(usuario, contrasena)
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible ingresar.')
    } finally {
      setEntrando(false)
    }
  }

  return (
    <div className={estilos.pantalla}>
      {/* ── La mitad de la marca ─────────────────────────────────────── */}
      <aside className={estilos.marca}>
        <div className={estilos.trama} aria-hidden="true" />
        <div className={estilos.selloGrande} aria-hidden="true">
          <LogoMega tamano={560} variante="marca" />
        </div>

        <div className={estilos.marcaContenido}>
          <LogoMega tamano={104} variante="completo" />
          <p className={estilos.nombre}>MEGA</p>
          <div className={estilos.reglaMarca} aria-hidden="true" />
          <p className={estilos.razon}>DISTRIBUCIONES Y PROYECTOS S.A.S.</p>
        </div>
      </aside>

      {/* ── La mitad del formulario ──────────────────────────────────── */}
      <main className={estilos.panel}>
        <div className={estilos.caja}>
          <h1 className={estilos.saludo}>Entra al sistema</h1>
          <p className={estilos.bajada}>Con tu usuario de MEGA.</p>

          <form className={estilos.formulario} onSubmit={(e) => void manejarEnvio(e)}>
            {error && (
              <div className={estilos.error}>
                <Aviso tipo="error">{error}</Aviso>
              </div>
            )}

            <Campo
              etiqueta="Usuario"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              ayuda="Tu inicial y tu apellido, como jbermudez. Si tu cuenta es de correo, escríbelo completo."
              obligatorio
              autoFocus
            />

            <Campo
              etiqueta="Contraseña"
              type="password"
              autoComplete="current-password"
              value={contrasena}
              onChange={(e) => setContrasena(e.target.value)}
              obligatorio
            />

            <Boton
              type="submit"
              variante="principal"
              tamano="grande"
              completo
              trabajando={entrando}
            >
              {entrando ? 'Ingresando…' : 'Ingresar'}
            </Boton>
          </form>

          {!esProductivo && (
            <p className={estilos.pie}>
              Ambiente de {etiquetaAmbiente[entorno.ambiente].toLowerCase()}
            </p>
          )}
        </div>
      </main>
    </div>
  )
}
